import { Injectable } from '@angular/core';
import { IDBPDatabase, openDB } from 'idb';
import { SWT_IDB_NAME, SWT_IDB_VERSION, SWT_STORE } from '../models/swt_constants';

/**
 * Low-level wrapper around the SWT IndexedDB database.
 *
 * Schema
 * ──────
 *  swt_users          – keyPath: 'Id'          (Users records)
 *  swt_tokens         – keyPath: 'id'          (SwtToken – one record, id = 1)
 *  swt_settings       – keyPath: 'key'         (arbitrary key/value settings)
 *  swt_building_areas     – keyPath: 'bld_int_ID', autoIncrement (BuildingAreas)
 *  swt_units_list         – keyPath: 'unt_int_UnitID', index: by_facility
 *  swt_walkthroughs       – keyPath: 'wkt_vch_WalkthroughCode', index: by_facility
 *  swt_unit_walkthroughs  – keyPath: 'unw_int_ID', autoIncrement, index: by_code
 *  swt_uwt                – keyPath: 'uwt_int_Id', autoIncrement, index: by_facility (UnitWalkThroughIdb)
 *  swt_fuwtd              – keyPath: 'fuwtd_int_Id', autoIncrement, indexes: by_walkthrough, by_facility (FacilityUnitWalkThroughDetailsIdb)
 *  swt_walkthrough_notes  – keyPath: 'wtn_int_Id', autoIncrement, index: by_code
 *  swt_walkthrough_list   – keyPath: 'wtl_int_Id', autoIncrement, indexes: by_facility, by_code (WalkthroughListIdb)
 *  swt_auction_details     – keyPath: 'auc_int_UnitID', index: by_facility (AuctionUnitRow)
 *  swt_work_orders         – keyPath: 'wor_int_ID', index: by_facility (WorkOrders)              [v11]
 *  swt_bld_wt_rows         – keyPath: 'bwr_int_Id' autoIncrement, index: by_walkthrough          [v11]
 *  swt_bwt                  – keyPath: 'bwt_int_Id' autoIncrement, indexes: by_facility, by_code   [v12]
 *  swt_bwtd                 – keyPath: 'bwtd_int_Id' autoIncrement, indexes: by_walkthrough, by_facility [v12]
 *  swt_unit_wt_photos       – keyPath: 'uwp_int_Id' autoIncrement, indexes: by_unit, by_walkthrough     [v13]
 *  swt_bwt_photos            – keyPath: 'bwtp_int_Id' autoIncrement, indexes: by_walkthrough, by_building_area [v15]
 *
 * Inject this service in higher-level services (SwtTokenService,
 * SwtUserService) rather than using it directly in components.
 */
@Injectable({ providedIn: 'root' })
export class SwtIndexedDbService {

  private readonly dbPromise: Promise<IDBPDatabase>;

  constructor() {
    this.dbPromise = openDB(SWT_IDB_NAME, SWT_IDB_VERSION, {
      /**
       * Called on THIS connection when another tab requests a newer version.
       * Close this connection so the waiting upgrade can proceed unblocked.
       * The next call that needs the DB will re-open it at the new version.
       */
      blocking(_currentVersion: number, _blockedVersion: number, event: IDBVersionChangeEvent): void {
        console.warn('[SWT IDB] Closing connection – newer version requested by another tab');
        (event.target as IDBDatabase).close();
      },
      /**
       * Called when THIS upgrade is blocked by an older open connection in
       * another tab.  Reload (online only) so the user gets the latest version
       * and the old connection is released automatically on unload.
       */
      blocked(_currentVersion: number, _blockedVersion: number, _event: IDBVersionChangeEvent): void {
        console.warn('[SWT IDB] Upgrade blocked by an older connection in another tab');
        if (navigator.onLine) {
          window.location.reload();
        }
      },
      upgrade(db, oldVersion, _newVersion, transaction) {
        // ── v21 → v22: add swt_auction_pending store ────────────────────────
        if (oldVersion < 22) {
          if (!db.objectStoreNames.contains(SWT_STORE.AUC_PENDING)) {
            const aupStore = db.createObjectStore(SWT_STORE.AUC_PENDING, { keyPath: 'aup_int_Id', autoIncrement: true });
            aupStore.createIndex('by_facility', 'aup_int_FacilityID', { unique: false });
            aupStore.createIndex('by_unit',     'aup_int_UnitID',     { unique: false });
          }
        }
        // ── v20 → v21: add swt_auc_photos store ─────────────────────────────────
        if (oldVersion < 21) {
          if (!db.objectStoreNames.contains(SWT_STORE.AUC_PHOTOS)) {
            const acpStore = db.createObjectStore(SWT_STORE.AUC_PHOTOS, { keyPath: 'acp_int_Id', autoIncrement: true });
            acpStore.createIndex('by_unit',        'acp_int_UnitID',             { unique: false });
            acpStore.createIndex('by_lease',       'acp_int_LeaseNo',            { unique: false });
            acpStore.createIndex('by_walkthrough', 'acp_vch_WalkthroughCode',    { unique: false });
          }
        }
        // ── v19 → v20: add swt_scan_history store ─────────────────────────────
        if (oldVersion < 20) {
          const schStore = db.createObjectStore(SWT_STORE.SCAN_HISTORY, { keyPath: 'sch_int_Id', autoIncrement: true });
          schStore.createIndex('by_facility',  'sch_int_FacilityId', { unique: false });
          schStore.createIndex('by_user',      'sch_int_UserId',     { unique: false });
          schStore.createIndex('by_scan_date', 'sch_dtm_ScanDate',   { unique: false });
          schStore.createIndex('by_scan_id',   'sch_vch_ScanId',     { unique: true  });
        }        // ── v18: recreate swt_work_orders with autoIncrement ─────────────────
        if (oldVersion < 18) {
          if (db.objectStoreNames.contains(SWT_STORE.WORK_ORDERS)) {
            db.deleteObjectStore(SWT_STORE.WORK_ORDERS);
          }
          const woStore18 = db.createObjectStore(SWT_STORE.WORK_ORDERS, { keyPath: 'wor_int_ID', autoIncrement: true });
          woStore18.createIndex('by_facility', 'wor_int_FacilityID', { unique: false });
        }
        // ── Create new stores if they don't exist yet ────────────────────────
        if (!db.objectStoreNames.contains(SWT_STORE.USERS)) {
          db.createObjectStore(SWT_STORE.USERS, { keyPath: 'Id' });
        }
        if (!db.objectStoreNames.contains(SWT_STORE.TOKENS)) {
          db.createObjectStore(SWT_STORE.TOKENS, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(SWT_STORE.SETTINGS)) {
          db.createObjectStore(SWT_STORE.SETTINGS, { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains(SWT_STORE.BUILDING_AREAS)) {
          db.createObjectStore(SWT_STORE.BUILDING_AREAS, { keyPath: 'bld_int_ID', autoIncrement: true });
        }
        if (!db.objectStoreNames.contains(SWT_STORE.UNITS_LIST)) {
          const unitStore = db.createObjectStore(SWT_STORE.UNITS_LIST, { keyPath: 'unt_int_UnitID' });
          unitStore.createIndex('by_facility', 'unt_int_FacilityID', { unique: false });
        }
        if (!db.objectStoreNames.contains(SWT_STORE.WALKTHROUGHS)) {
          const wtStore = db.createObjectStore(SWT_STORE.WALKTHROUGHS, { keyPath: 'wkt_vch_WalkthroughCode' });
          wtStore.createIndex('by_facility', 'wkt_int_FacilityID', { unique: false });
        }
        if (!db.objectStoreNames.contains(SWT_STORE.UNIT_WALKTHROUGHS)) {
          const unwStore = db.createObjectStore(SWT_STORE.UNIT_WALKTHROUGHS, { keyPath: 'unw_int_ID', autoIncrement: true });
          unwStore.createIndex('by_code', 'unw_vch_WalkthroughCode', { unique: false });
        }
        if (!db.objectStoreNames.contains(SWT_STORE.UWT)) {
          const uwtStore = db.createObjectStore(SWT_STORE.UWT, { keyPath: 'uwt_int_Id', autoIncrement: true });
          uwtStore.createIndex('by_facility', 'uwt_int_FacilityID', { unique: false });
        }
        if (!db.objectStoreNames.contains(SWT_STORE.FUWTD)) {
          const fuwtdStore = db.createObjectStore(SWT_STORE.FUWTD, { keyPath: 'fuwtd_int_Id', autoIncrement: true });
          fuwtdStore.createIndex('by_walkthrough', 'fuwtd_int_WalkThroughId', { unique: false });
          fuwtdStore.createIndex('by_facility',    'fuwtd_int_FacilityId',    { unique: false });
        }
        if (!db.objectStoreNames.contains(SWT_STORE.WALKTHROUGH_NOTES)) {
          const wtnStore = db.createObjectStore(SWT_STORE.WALKTHROUGH_NOTES, { keyPath: 'wtn_int_Id', autoIncrement: true });
          wtnStore.createIndex('by_code', 'wtn_vch_WalkthroughCode', { unique: false });
        }
        if (!db.objectStoreNames.contains(SWT_STORE.WALKTHROUGH_LIST)) {
          const wtlStore = db.createObjectStore(SWT_STORE.WALKTHROUGH_LIST, { keyPath: 'wtl_int_Id', autoIncrement: true });
          wtlStore.createIndex('by_facility', 'wtl_int_FacilityID',        { unique: false });
          wtlStore.createIndex('by_code',     'wtl_vch_WalkthroughCode',   { unique: true  });
        }
        if (!db.objectStoreNames.contains(SWT_STORE.AUCTION_DETAILS)) {
          const aucStore = db.createObjectStore(SWT_STORE.AUCTION_DETAILS, { keyPath: 'auc_int_UnitID' });
          aucStore.createIndex('by_facility', 'auc_int_FacilityID', { unique: false });
        }
        if (!db.objectStoreNames.contains(SWT_STORE.WORK_ORDERS)) {
          const woStore = db.createObjectStore(SWT_STORE.WORK_ORDERS, { keyPath: 'wor_int_ID', autoIncrement: true });
          woStore.createIndex('by_facility', 'wor_int_FacilityID', { unique: false });
        }
        if (!db.objectStoreNames.contains(SWT_STORE.BLD_WT_ROWS)) {
          const bwrStore = db.createObjectStore(SWT_STORE.BLD_WT_ROWS, { keyPath: 'bwr_int_Id', autoIncrement: true });
          bwrStore.createIndex('by_walkthrough', 'bwr_int_WalkThroughId', { unique: false });
        }
        if (!db.objectStoreNames.contains(SWT_STORE.BWT)) {
          const bwtStore = db.createObjectStore(SWT_STORE.BWT, { keyPath: 'bwt_int_Id', autoIncrement: true });
          bwtStore.createIndex('by_facility', 'bwt_int_FacilityID', { unique: false });
        }
        if (!db.objectStoreNames.contains(SWT_STORE.BWTD)) {
          const bwtdStore = db.createObjectStore(SWT_STORE.BWTD, { keyPath: 'bwtd_int_Id', autoIncrement: true });
          bwtdStore.createIndex('by_walkthrough', 'bwtd_int_WalkThroughId', { unique: false });
          bwtdStore.createIndex('by_facility',    'bwtd_int_FacilityId',    { unique: false });
        }
        if (!db.objectStoreNames.contains(SWT_STORE.UNIT_WT_PHOTOS)) {
          const uwpStore = db.createObjectStore(SWT_STORE.UNIT_WT_PHOTOS, { keyPath: 'uwp_int_Id', autoIncrement: true });
          uwpStore.createIndex('by_unit',        'uwp_int_UnitID',             { unique: false });
          uwpStore.createIndex('by_walkthrough',  'uwp_vch_WalkthroughCode',   { unique: false });
        }
        if (!db.objectStoreNames.contains(SWT_STORE.SYNC_TRACKER)) {
          // keyPath = 'synt_vch_tablename' — one row per domain table name.
          db.createObjectStore(SWT_STORE.SYNC_TRACKER, { keyPath: 'synt_vch_tablename' });
        }
        if (!db.objectStoreNames.contains(SWT_STORE.BWT_PHOTOS)) {
          const bwtpStore = db.createObjectStore(SWT_STORE.BWT_PHOTOS, { keyPath: 'bwtp_int_Id', autoIncrement: true });
          bwtpStore.createIndex('by_walkthrough',   'bwtp_int_WalkThroughId',  { unique: false });
          bwtpStore.createIndex('by_building_area', 'bwtp_int_BuildingAreaId', { unique: false });
        }
        if (!db.objectStoreNames.contains(SWT_STORE.WO_PHOTOS)) {
          const wopStore = db.createObjectStore(SWT_STORE.WO_PHOTOS, { keyPath: 'wop_int_ID', autoIncrement: true });
          wopStore.createIndex('by_work_order', 'wop_vch_WorkOrderCode', { unique: false });
          wopStore.createIndex('by_facility',   'wop_int_FacilityID',    { unique: false });
        }
        if (!db.objectStoreNames.contains(SWT_STORE.AUC_PHOTOS)) {
          const acpStore = db.createObjectStore(SWT_STORE.AUC_PHOTOS, { keyPath: 'acp_int_Id', autoIncrement: true });
          acpStore.createIndex('by_unit',        'acp_int_UnitID',             { unique: false });
          acpStore.createIndex('by_lease',       'acp_int_LeaseNo',            { unique: false });
          acpStore.createIndex('by_walkthrough', 'acp_vch_WalkthroughCode',    { unique: false });
        }
        if (!db.objectStoreNames.contains(SWT_STORE.SCAN_HISTORY)) {
          const schStore = db.createObjectStore(SWT_STORE.SCAN_HISTORY, { keyPath: 'sch_int_Id', autoIncrement: true });
          schStore.createIndex('by_facility',  'sch_int_FacilityId', { unique: false });
          schStore.createIndex('by_user',      'sch_int_UserId',     { unique: false });
          schStore.createIndex('by_scan_date', 'sch_dtm_ScanDate',   { unique: false });
          schStore.createIndex('by_scan_id',   'sch_vch_ScanId',     { unique: true  });
        }
        if (!db.objectStoreNames.contains(SWT_STORE.AUC_PENDING)) {
          const aupStore = db.createObjectStore(SWT_STORE.AUC_PENDING, { keyPath: 'aup_int_Id', autoIncrement: true });
          aupStore.createIndex('by_facility', 'aup_int_FacilityID', { unique: false });
          aupStore.createIndex('by_unit',     'aup_int_UnitID',     { unique: false });
        }

        // ── Patch missing indexes on existing stores (safe to run on every upgrade) ──
        // Uses the version-change transaction — the only context where existing
        // stores can have indexes added without recreating them.
        const _addIndex = (storeName: string, indexName: string, keyPath: string, opts?: IDBIndexParameters) => {
          if (!db.objectStoreNames.contains(storeName)) { return; }
          const s = transaction.objectStore(storeName);
          if (!s.indexNames.contains(indexName)) {
            s.createIndex(indexName, keyPath, opts ?? { unique: false });
          }
        };
        _addIndex(SWT_STORE.UNITS_LIST,        'by_facility',    'unt_int_FacilityID');
        _addIndex(SWT_STORE.WALKTHROUGHS,       'by_facility',    'wkt_int_FacilityID');
        _addIndex(SWT_STORE.UNIT_WALKTHROUGHS,  'by_code',        'unw_vch_WalkthroughCode');
        _addIndex(SWT_STORE.UWT,                'by_facility',    'uwt_int_FacilityID');
        _addIndex(SWT_STORE.FUWTD,              'by_walkthrough', 'fuwtd_int_WalkThroughId');
        _addIndex(SWT_STORE.FUWTD,              'by_facility',    'fuwtd_int_FacilityId');
        _addIndex(SWT_STORE.WALKTHROUGH_NOTES,  'by_code',        'wtn_vch_WalkthroughCode');
        _addIndex(SWT_STORE.WALKTHROUGH_LIST,   'by_facility',    'wtl_int_FacilityID');
        _addIndex(SWT_STORE.WALKTHROUGH_LIST,   'by_code',        'wtl_vch_WalkthroughCode', { unique: true });
        _addIndex(SWT_STORE.AUCTION_DETAILS,    'by_facility',    'auc_int_FacilityID');
        _addIndex(SWT_STORE.WORK_ORDERS,         'by_facility',    'wor_int_FacilityID');
        _addIndex(SWT_STORE.BLD_WT_ROWS,         'by_walkthrough', 'bwr_int_WalkThroughId');
        _addIndex(SWT_STORE.BWT,                  'by_facility',    'bwt_int_FacilityID');
        _addIndex(SWT_STORE.BWTD,                 'by_walkthrough', 'bwtd_int_WalkThroughId');
        _addIndex(SWT_STORE.BWTD,                 'by_facility',    'bwtd_int_FacilityId');
        _addIndex(SWT_STORE.UNIT_WT_PHOTOS,        'by_unit',          'uwp_int_UnitID');
        _addIndex(SWT_STORE.UNIT_WT_PHOTOS,        'by_walkthrough',   'uwp_vch_WalkthroughCode');
        _addIndex(SWT_STORE.UNIT_WT_PHOTOS,        'by_fuwtd_local_id','uwp_int_FuwtdLocalId');
        _addIndex(SWT_STORE.BWT_PHOTOS,            'by_walkthrough',   'bwtp_int_WalkThroughId');
        _addIndex(SWT_STORE.BWT_PHOTOS,            'by_building_area', 'bwtp_int_BuildingAreaId');
        _addIndex(SWT_STORE.WO_PHOTOS,             'by_work_order',    'wop_vch_WorkOrderCode');
        _addIndex(SWT_STORE.WO_PHOTOS,             'by_facility',      'wop_int_FacilityID');
        _addIndex(SWT_STORE.SCAN_HISTORY,          'by_facility',      'sch_int_FacilityId');
        _addIndex(SWT_STORE.AUC_PHOTOS,            'by_unit',          'acp_int_UnitID');
        _addIndex(SWT_STORE.AUC_PHOTOS,            'by_lease',         'acp_int_LeaseNo');
        _addIndex(SWT_STORE.AUC_PHOTOS,            'by_walkthrough',   'acp_vch_WalkthroughCode');
        _addIndex(SWT_STORE.AUC_PENDING,           'by_facility',      'aup_int_FacilityID');
        _addIndex(SWT_STORE.AUC_PENDING,           'by_unit',          'aup_int_UnitID');
        _addIndex(SWT_STORE.SCAN_HISTORY,          'by_user',          'sch_int_UserId');
        _addIndex(SWT_STORE.SCAN_HISTORY,          'by_scan_date',     'sch_dtm_ScanDate');
        _addIndex(SWT_STORE.SCAN_HISTORY,          'by_scan_id',       'sch_vch_ScanId', { unique: true });
      },
    });
  }

  /** Insert or replace a record in the given store. */
  async put<T>(store: string, data: T): Promise<IDBValidKey> {
    const db = await this.dbPromise;
    return db.put(store, data);
  }

  /**
   * Insert a new record into an autoIncrement store.
   * The keyPath field must NOT be present (or must be undefined) so the
   * store generates a fresh key.  Returns the generated numeric key.
   */
  async add<T>(store: string, data: T): Promise<IDBValidKey> {
    const db = await this.dbPromise;
    return db.add(store, data);
  }

  /** Retrieve a single record by primary key. */
  async get<T>(store: string, key: IDBValidKey): Promise<T | undefined> {
    const db = await this.dbPromise;
    return db.get(store, key);
  }

  /** Delete a record by primary key. */
  async delete(store: string, key: IDBValidKey): Promise<void> {
    const db = await this.dbPromise;
    return db.delete(store, key);
  }

  /** Return all records in the store. */
  async getAll<T>(store: string): Promise<T[]> {
    const db = await this.dbPromise;
    return db.getAll(store);
  }

  /** Return all records matching an index key (e.g. getByIndex('swt_units_list', 'by_facility', 1)). */
  async getByIndex<T>(store: string, indexName: string, key: IDBValidKey): Promise<T[]> {
    const db = await this.dbPromise;
    return db.getAllFromIndex(store, indexName, key);
  }

  /** Remove every record from the store (keeps the store itself). */
  async clear(store: string): Promise<void> {
    const db = await this.dbPromise;
    return db.clear(store);
  }

  /**
   * Expose the raw IDBPDatabase handle for callers that need to manage
   * their own transaction (e.g. bulk replace in one atomic commit).
   */
  getDb(): Promise<IDBPDatabase> {
    return this.dbPromise;
  }

  /**
   * Returns true when the browser storage quota is critically low (< 10 MB remaining
   * or < 5 % remaining), so callers can warn the user before attempting large writes.
   * Falls back to false when the StorageManager API is unavailable (e.g. older browsers).
   */
  async isStorageLow(): Promise<boolean> {
    if (!navigator.storage?.estimate) { return false; }
    try {
      const { usage = 0, quota = 0 } = await navigator.storage.estimate();
      if (quota === 0) { return false; }
      const remaining = quota - usage;
      const remainingMb = remaining / (1024 * 1024);
      const usedPercent = (usage / quota) * 100;
      return remainingMb < 10 || usedPercent > 95;
    } catch {
      return false;
    }
  }
}
